import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoLockClosedOutline, IoLogOutOutline } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { PARTNER_SIGNUP_CATEGORIES } from '../../../config/ieumConfig';
import ChangePasswordModal from '../../Login/ChangePasswordModal';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const formatPhone = (val) => {
  const d = (val || '').replace(/[^0-9]/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const PartnerMyPage = () => {
  const navigate = useNavigate();
  const { userData, signOut } = useAuth();

  const [form, setForm] = useState({
    businessName: '',
    bizNumber: '',
    businessType: '',
    openDate: '',
    ntsStatus: '',
    ntsTaxType: '',
    ntsEndDate: '',
    ownerName: '',
    representativePhone: '',
    address: '',
    businessPhone: '',
    managerName: '',
    managerPhone: '',
    managerEmail: '',
    category: '이사',
    licenseUrl: '',
    licenseFileName: '',
  });
  const [pwModal, setPwModal] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userData) {
      const cats = userData.service_categories || [];
      const categoryLabel = cats.length > 0
        ? cats.map((id) => PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === id)?.label || id).join(', ')
        : '-';

      const bizTypeRaw = userData.business_type || userData.ocr_business_type || '';
      const bizTypeLabel = bizTypeRaw === 'corporation' ? '법인'
        : bizTypeRaw === 'sole_proprietor' ? '개인'
        : '';
      const openDateRaw = String(userData.open_date || userData.ocr_open_date || '').replace(/\D/g, '');
      const openDateLabel = openDateRaw.length === 8
        ? `${openDateRaw.slice(0, 4)}-${openDateRaw.slice(4, 6)}-${openDateRaw.slice(6)}`
        : '';

      const ntsEndRaw = String(userData.nts_end_dt || '').replace(/\D/g, '');
      const ntsEndLabel = ntsEndRaw.length === 8
        ? `${ntsEndRaw.slice(0, 4)}-${ntsEndRaw.slice(4, 6)}-${ntsEndRaw.slice(6)}`
        : '';

      setForm((prev) => ({
        ...prev,
        businessName: userData.business_name || '',
        bizNumber: userData.business_number || userData.biz_number || '',
        businessType: bizTypeLabel,
        openDate: openDateLabel,
        ntsStatus: userData.nts_b_stt || '',
        ntsTaxType: userData.nts_tax_type || '',
        ntsEndDate: ntsEndLabel,
        ownerName: userData.owner_name || userData.representative_name || '',
        representativePhone: userData.representative_phone || '',
        address: userData.address || userData.business_address || '',
        businessPhone: userData.business_phone || userData.office_phone || '',
        managerName: userData.manager_name || '',
        managerPhone: userData.manager_phone || '',
        managerEmail: userData.manager_email || userData.email || '',
        category: categoryLabel,
        licenseUrl: userData.business_license_url || userData.license_url || '',
        licenseFileName: userData.license_file_name || '사업자등록증',
      }));
    }
  }, [userData]);

  const handleField = (key) => (e) => {
    const value = ['businessPhone', 'managerPhone'].includes(key)
      ? formatPhone(e.target.value)
      : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!userData?.uid) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'partners', userData.uid), {
        manager_phone: form.managerPhone,
        updated_at: serverTimestamp(),
      });
      await updateDoc(doc(db, 'users', userData.uid), {
        name: form.managerName,
        phone: form.managerPhone,
      });
      alert('업체 정보가 변경되었습니다.');
    } catch (err) {
      console.error('save failed', err);
      alert('저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('정말 탈퇴하시겠습니까?\n\n탈퇴 후에는 동일한 이메일/전화번호로 재가입이 불가합니다.')) {
      return;
    }
    try {
      if (userData?.uid) {
        await updateDoc(doc(db, 'users', userData.uid), {
          status: 'withdrawn',
          withdrawn_at: serverTimestamp(),
        });
      }
      await signOut();
      navigate('/partner/login', { replace: true });
    } catch (err) {
      alert('탈퇴 처리에 실패했습니다.');
    }
  };

  return (
    <Wrap>
      <PageTitle>마이페이지</PageTitle>
      <PageDesc>
        업체 정보 수정, 비밀번호 변경, 회원 탈퇴를 할 수 있습니다.<br />
        담당자 정보 변경 후 [변경 완료] 버튼을 눌러 주세요.
      </PageDesc>

      <Card>
        <CardHeader>업체 정보 수정</CardHeader>

        <form onSubmit={handleSave}>
          <Field>
            <Label>업체명</Label>
            <Input value={form.businessName} readOnly />
            <FieldNote>업체명 변경은 본사에 문의하세요.</FieldNote>
          </Field>

          <Field>
            <Label>사업자등록번호</Label>
            <Input value={form.bizNumber || '-'} readOnly />
          </Field>

          <Field>
            <Label>사업자 종류</Label>
            <Input value={form.businessType || '-'} readOnly />
          </Field>

          <Field>
            <Label>개업연월일</Label>
            <Input value={form.openDate || '-'} readOnly />
          </Field>

          <Field>
            <Label>사업자상태</Label>
            <Input value={form.ntsStatus || '-'} readOnly />
            <FieldNote>국세청 자동검증 결과입니다.</FieldNote>
          </Field>

          <Field>
            <Label>과세유형</Label>
            <Input value={form.ntsTaxType || '-'} readOnly />
          </Field>

          {form.ntsEndDate && (
            <Field>
              <Label>폐업일자</Label>
              <Input value={form.ntsEndDate} readOnly />
            </Field>
          )}

          <Field>
            <Label>대표자명</Label>
            <Input value={form.ownerName} readOnly />
            <FieldNote>대표자 변경은 본사에 문의하세요.</FieldNote>
          </Field>

          <Field>
            <Label>대표자 연락처</Label>
            <Input value={form.representativePhone || '-'} readOnly />
            <FieldNote>대표자 연락처 변경은 본사에 문의하세요.</FieldNote>
          </Field>

          <Field>
            <Label>주소</Label>
            <Input value={form.address} readOnly />
            <FieldNote>주소 변경은 본사에 문의하세요.</FieldNote>
          </Field>

          <Field>
            <Label>사업장 연락처</Label>
            <Input value={form.businessPhone || '-'} readOnly />
            <FieldNote>사업장 연락처 변경은 본사에 문의하세요.</FieldNote>
          </Field>

          <Field>
            <Label>담당자명</Label>
            <Input value={form.managerName} onChange={handleField('managerName')} />
          </Field>

          <Field>
            <Label>담당자 연락처</Label>
            <Input value={form.managerPhone} onChange={handleField('managerPhone')} />
          </Field>

          <Field>
            <Label>담당자 이메일</Label>
            <Input type="email" value={form.managerEmail} onChange={handleField('managerEmail')} />
          </Field>

          <Field>
            <Label>업종</Label>
            <Input value={form.category} readOnly />
            <FieldNote>업종 변경은 본사에 문의하세요.</FieldNote>
          </Field>

          <Field>
            <Label>사업자등록증</Label>
            {form.licenseUrl ? (
              <LicenseLink href={form.licenseUrl} target="_blank" rel="noopener noreferrer">
                {form.licenseFileName} 보기
              </LicenseLink>
            ) : (
              <Input value="등록된 파일 없음" readOnly />
            )}
            <FieldNote>파일 변경은 본사에 문의하세요.</FieldNote>
          </Field>

          <SubmitBtn type="submit" disabled={saving}>
            {saving ? '저장 중...' : '변경 완료'}
          </SubmitBtn>
        </form>

        <PwLinkRow>
          <PwLink type="button" onClick={() => setPwModal(true)}>
            <IoLockClosedOutline size={14} />
            비밀번호 변경
          </PwLink>
        </PwLinkRow>
      </Card>

      <WithdrawCard>
        <WithdrawHeader>회원 탈퇴</WithdrawHeader>
        <WithdrawDesc>
          탈퇴 시 계정이 비활성화되며, 더 이상 로그인할 수 없습니다.<br />
          탈퇴 후에도 가입 기록 내역은 관리자 측에서 보관합니다.
        </WithdrawDesc>
        <WithdrawBtn type="button" onClick={handleWithdraw}>
          <IoLogOutOutline size={16} />
          회원 탈퇴
        </WithdrawBtn>
      </WithdrawCard>

      {pwModal && (
        <ChangePasswordModal
          uid={userData?.uid}
          dismissible
          onClose={() => setPwModal(false)}
          onSuccess={() => {
            setPwModal(false);
            alert('비밀번호가 변경되었습니다.');
          }}
        />
      )}
    </Wrap>
  );
};

export default PartnerMyPage;

// ───── 스타일 ─────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    max-width: 720px;
    margin: 0 auto;
    width: 100%;
  }
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs};

  @media (min-width: 1024px) {
    font-size: 22px;
    text-align: center;
    margin-bottom: 8px;
  }
`;

const PageDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.lg};

  @media (min-width: 1024px) {
    text-align: center;
    margin-bottom: 20px;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CardHeader = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.base};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.bgWhite};
  outline: none;

  &:read-only {
    background: #F3F4F6;
    color: ${({ theme }) => theme.colors.textSecondary};
    cursor: not-allowed;
  }

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
  &[readonly] { background: ${({ theme }) => theme.colors.borderLight}; cursor: not-allowed; }
`;

const FieldNote = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 4px 0 0;
`;

const LicenseLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; text-decoration: underline; }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PwLinkRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const PwLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  padding: 8px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
`;

const WithdrawCard = styled.div`
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const WithdrawHeader = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #991B1B;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const WithdrawDesc = styled.p`
  font-size: 12px;
  color: #B91C1C;
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.base};
`;

const WithdrawBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  width: 100%;
  padding: 12px;
  background: #fff;
  border: 1px solid #FCA5A5;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: #DC2626;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
`;
